/**
 * Quiz State Management
 * Pure functions for state management - no classes needed
 */

/**
 * Quiz navigation constants
 */
const QUIZ_NAVIGATION = {
	RESULT: 'result',
	BACK: 'go-back'
};

/**
 * Create initial quiz state
 * @returns {Object} Initial state object
 */
function createQuizState() {
	return {
		answers: {},
		history: [],
		currentQuestion: 1
	};
}

/**
 * Set answer in state (pure function)
 * @param {Object} state - Current state
 * @param {string} key - Question key
 * @param {string} value - Answer value
 * @returns {Object} New state
 */
function setAnswer(state, key, value) {
	if (!state || typeof state !== 'object') {
		throw new Error('setAnswer: state must be an object');
	}
	if (!state.answers || typeof state.answers !== 'object') {
		throw new Error('setAnswer: state.answers must be an object');
	}
	if (typeof key !== 'string' || key.trim() === '') {
		throw new Error('setAnswer: key must be a non-empty string');
	}
	if (value === undefined || value === null) {
		throw new Error('setAnswer: value must be defined');
	}

	return {
		...state,
		answers: { ...state.answers, [key]: value }
	};
}

/**
 * Save current state to history
 * @param {Object} state - Current state
 * @returns {Object} New state with updated history
 */
function saveToHistory(state) {
	if (!state || typeof state !== 'object') {
		throw new Error('saveToHistory: state must be an object');
	}
	if (!Array.isArray(state.history)) {
		throw new Error('saveToHistory: state.history must be an array');
	}
	if (!state.answers || typeof state.answers !== 'object') {
		throw new Error('saveToHistory: state.answers must be an object');
	}
	if (typeof state.currentQuestion !== 'number') {
		throw new Error('saveToHistory: state.currentQuestion must be a number');
	}

	return {
		...state,
		history: [
			...state.history,
			{
				currentQuestion: state.currentQuestion,
				answers: { ...state.answers }
			}
		]
	};
}

/**
 * Restore previous state from history
 * @param {Object} state - Current state
 * @returns {Object} Previous state or current if no history
 */
function restoreFromHistory(state) {
	if (!state || typeof state !== 'object') {
		throw new Error('restoreFromHistory: state must be an object');
	}
	if (!Array.isArray(state.history)) {
		throw new Error('restoreFromHistory: state.history must be an array');
	}

	if (state.history.length === 0) return state;

	const history = [...state.history];
	const previousState = history.pop();

	return {
		...previousState,
		history
	};
}

/**
 * Set current question
 * @param {Object} state - Current state
 * @param {number} questionNum - Question number
 * @returns {Object} New state
 */
function setCurrentQuestion(state, questionNum) {
	if (!state || typeof state !== 'object') {
		throw new Error('setCurrentQuestion: state must be an object');
	}
	if (typeof questionNum !== 'number' || !Number.isInteger(questionNum) || questionNum < 1) {
		throw new Error('setCurrentQuestion: questionNum must be a positive integer');
	}

	return {
		...state,
		currentQuestion: questionNum
	};
}

/**
 * Quiz Results - Pure functions for calculating results
 */

/**
 * Calculate quiz result based on answers
 * @param {Object} answers - User answers
 * @returns {string} The recommended annuity type
 */
function calculateResult(answers) {
	// Validate input
	if (!answers || typeof answers !== 'object') {
		console.error('calculateResult: invalid answers object', { answers });
		return 'Fixed Index Annuity'; // Safe fallback
	}

	const { goal, start, risk } = answers;

	// Log if we're making decisions with missing data
	if (!goal) {
		console.warn('calculateResult called without goal', { answers });
	}

	switch (goal) {
		case 'income':
			if (start === 'now') {
				return 'SPIA';
			}
			return 'Deferred Income Annuity';

		case 'growth':
			return 'Fixed Annuity (MYGA)';

		case 'market':
			if (risk === 'high') {
				return 'Variable Annuity';
			}
			return 'Fixed Index Annuity';

		default:
			return 'Fixed Index Annuity';
	}
}

/**
 * Get result message for specific annuity type
 * @param {string} resultType - The annuity type
 * @returns {string} Descriptive message for the result
 */
function getResultMessage(resultType) {
	const messages = {
		SPIA: 'You may benefit from an Immediate Annuity (SPIA), which starts paying income right away — ideal for those already retired.',
		'Deferred Income Annuity':
			'A Deferred Income Annuity offers guaranteed income starting later — perfect for planning ahead.',
		'Fixed Index Annuity':
			'A Fixed Index Annuity offers potential market-linked returns with downside protection.',
		'Fixed Annuity (MYGA)':
			'A Fixed Annuity (MYGA) provides guaranteed interest rates for steady, predictable growth.',
		'Variable Annuity':
			'A Variable Annuity offers investment flexibility with market participation — ideal for growth-focused investors.'
	};

	return messages[resultType] || messages['Fixed Index Annuity'];
}

/**
 * Get list of questions the user actually answered with their responses
 * @param {Object} answers - User answers
 * @returns {Array<Object>} Array of objects with question and answer pairs
 */
function getQuestionsAnswered(answers) {
	const questionTexts = {
		retire: 'When do you plan to retire?',
		goal: "What's your primary goal for this annuity?",
		start: 'When would you like your income to begin?',
		risk: 'How comfortable are you with investment risk?',
		lifetime: 'Do you want this annuity to provide income for life?',
		legacy: 'How important is leaving money to heirs?',
		fees: 'How do you feel about fees?'
	};

	return Object.keys(answers).map((questionId) => ({
		question: questionTexts[questionId] || questionId,
		answer: answers[questionId]
	}));
}

/**
 * Quiz Progress - Functions for progress bar management
 */

/**
 * Set initial progress state to prevent visual flicker
 * @param {HTMLElement} container - Quiz container
 */
function setInitialProgress(container) {
	const progressFill = container.querySelector('#progressFill');
	if (!progressFill) return;

	progressFill.style.setProperty('--progress-width', '0%');
	progressFill.style.transition = 'none';

	requestAnimationFrame(() => {
		progressFill.style.transition = 'width 0.3s ease';
	});
}

/**
 * Update progress bar based on completed questions
 * @param {HTMLElement} container - Quiz container
 * @param {number} completedQuestions - Number of completed questions
 * @param {Object} config - Quiz configuration
 */
function updateProgress(container, completedQuestions, config) {
	const progressFill = container.querySelector('#progressFill');
	if (!progressFill) return;

	const expectedTotalQuestions = config.expectedAnswers;
	const progress = Math.min(
		(completedQuestions / expectedTotalQuestions) * 100,
		100
	);

	const currentProgress =
		progressFill.style.getPropertyValue('--progress-width') || '0%';
	const newProgress = `${Math.round(progress)}%`;

	if (currentProgress !== newProgress) {
		progressFill.style.setProperty('--progress-width', newProgress);
	}
}

/**
 * Set progress bar to 100% completion
 * @param {HTMLElement} container - Quiz container
 */
function setProgressTo100(container) {
	const progressFill = container.querySelector('#progressFill');
	if (!progressFill) return;

	const currentProgress =
		progressFill.style.getPropertyValue('--progress-width') || '0%';
	const newProgress = '100%';

	if (currentProgress !== newProgress) {
		progressFill.style.setProperty('--progress-width', newProgress);
	}
}

/**
 * Quiz Form Submission - Pure functions for form handling
 */

/**
 * Validate email address format
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid email format
 */
function validateEmail(email) {
	const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return emailPattern.test(email);
}

/**
 * Sanitize text input to prevent XSS and injection attacks
 * @param {string} input - Text input to sanitize
 * @returns {string} Sanitized text
 */
function sanitizeInput(input) {
	if (typeof input !== 'string') return '';
	
	return input
		.trim()
		// Remove HTML tags and dangerous characters
		.replace(/[<>]/g, '')
		// Remove quotes that could break HTML attributes
		.replace(/["'`]/g, '')
		// Remove ampersands to prevent HTML entity injection
		.replace(/&/g, '')
		// Remove backslashes that could be used for escaping
		.replace(/\\/g, '')
		// eslint-disable-next-line no-control-regex -- stripping null bytes and control characters is the point
		.replace(/[\x00-\x1F\x7F]/g, '')
		// Remove potential JavaScript protocol
		.replace(/javascript:/gi, '')
		// Remove data URLs
		.replace(/data:/gi, '')
		// Limit length to prevent buffer overflow attacks
		.slice(0, 500);
}

/**
 * Get and validate email from form
 * @param {HTMLElement} container - Quiz container
 * @returns {string|null} Validated email or null if invalid
 */
function getValidatedEmail(container) {
	const emailInput = container.querySelector(
		'.email_capture input[type="email"]'
	);
	const rawEmail = emailInput?.value || '';
	const email = sanitizeInput(rawEmail);

	if (!email) {
		alert('Please enter your email address.');
		return null;
	}

	if (!validateEmail(email)) {
		alert('Please enter a valid email address.');
		return null;
	}

	return email;
}

/**
 * Submit quiz data to Zapier
 * @param {Object} quizData - Complete quiz data
 * @returns {Promise} Fetch promise
 */
function submitToZapier(quizData) {
	const zapierWebhookUrl =
		'https://hooks.zapier.com/hooks/catch/3626579/uhvfdxu/';

	const formData = new FormData();
	formData.append('email', quizData.email);
	formData.append('result', quizData.result);
	formData.append('timestamp', quizData.timestamp);
	formData.append('answers', JSON.stringify(quizData.answers));
	formData.append(
		'questionsAnswered',
		JSON.stringify(quizData.questionsAnswered)
	);

	// Note: mode: 'no-cors' required for Zapier webhooks
	// This means we cannot read response, but network errors will still throw
	return fetch(zapierWebhookUrl, {
		method: 'POST',
		mode: 'no-cors',
		body: formData
	}).catch(error => {
		// Network-level errors will throw here
		throw new Error(`Zapier submission failed: ${error.message}`);
	});
}

/**
 * Quiz UI - Pure functions for DOM manipulation
 */

/**
 * Show specific question and hide others
 * @param {HTMLElement} container - Quiz container
 * @param {number} questionNum - Question number to display
 */
function showQuestion(container, questionNum) {
	const questions = container.querySelectorAll('.question');
	questions.forEach((question) => question.classList.remove('active'));

	const result = container.querySelector('.result');
	const emailCapture = container.querySelector('.email_capture');
	if (result) result.style.display = 'none';
	if (emailCapture) emailCapture.style.display = 'none';

	const targetQuestion = container.querySelector(
		`[data-question="${questionNum}"]`
	);
	if (targetQuestion) {
		targetQuestion.classList.add('active');
	} else {
		console.error(`Question ${questionNum} not found in DOM`, {
			questionNum,
			availableQuestions: Array.from(questions).map(q => q.dataset.question)
		});
	}
}

/**
 * Update quiz title with result type
 * @param {HTMLElement} container - Quiz container
 * @param {string} resultType - The annuity type to include in title
 */
function updateTitle(container, resultType) {
	const titleElement = container.querySelector('h1');
	if (titleElement) {
		titleElement.textContent = `Find the Right ${resultType} for You`;
	}
}

/**
 * Display quiz result and email capture
 * @param {HTMLElement} container - Quiz container
 * @param {string} resultMessage - Result message to display
 */
function showResult(container, resultMessage) {
	const questions = container.querySelectorAll('.question');
	questions.forEach((question) => question.classList.remove('active'));

	const result = container.querySelector('.result');
	if (result) {
		// Use textContent for security instead of innerHTML
		const p = document.createElement('p');
		p.textContent = resultMessage;
		result.innerHTML = ''; // Clear previous content
		result.appendChild(p);
		result.style.display = 'block';
	}

	const emailCapture = container.querySelector('.email_capture');
	if (emailCapture) {
		emailCapture.style.display = 'block';
	}
}

/**
 * Show submission success message
 * @param {HTMLElement} container - Quiz container
 */
function showSubmissionSuccess(container) {
	const emailCapture = container.querySelector('.email_capture');
	if (!emailCapture) return;

	// Hide the form
	const emailForm = emailCapture.querySelector('.email_form');
	if (emailForm) {
		emailForm.style.display = 'none';
	}

	// Show the success message
	const successMessage = emailCapture.querySelector('.submission_success');
	if (successMessage) {
		successMessage.style.display = 'block';
	}
}

/**
 * Main Quiz Controller - Simple class for state management and coordination
 * Only uses a class because it needs to maintain state across interactions
 *
 * @class AnnuityQuiz
 */
class AnnuityQuiz {
	/**
	 * Initialize the Quiz Controller
	 * @constructs AnnuityQuiz
	 */
	constructor() {
		this.container = document.querySelector('[data-component="annuity-quiz"]');
		if (!this.container) return;

		// Load quiz configuration
		this.config = this.loadQuizConfiguration();

		// Initialize state (the only thing that needs to persist)
		this.state = createQuizState();

		// Initialize processing flag for debouncing
		this.isProcessing = false;

		this.init();
	}

	/**
	 * Load quiz configuration from JSON script tag
	 * @returns {Object} Quiz configuration object
	 * @private
	 */
	loadQuizConfiguration() {
		const defaultConfig = {
			totalQuestions: 7,
			expectedAnswers: 6,
			quizPaths: {
				income: ['1','2','3','5','6','7'],
				growth: ['1','2','4','5','6','7'],
				market: ['1','2','4','5','6','7']
			},
			questions: []
		};

		try {
			// Read from JSON script tag instead of data attribute (eliminates 250-char truncation)
			const configScript = document.getElementById('annuity-quiz-config');

			if (!configScript) {
				console.warn('Quiz configuration script not found, using defaults');
				return defaultConfig;
			}

			const configData = configScript.textContent;

			if (!configData || configData.trim() === '') {
				console.warn('Quiz configuration is empty, using defaults');
				return defaultConfig;
			}

			const parsedConfig = JSON.parse(configData);

			// Validate critical configuration properties
			if (!parsedConfig.totalQuestions || !parsedConfig.expectedAnswers) {
				console.error('Invalid quiz configuration structure', {
					config: parsedConfig,
					missingFields: {
						totalQuestions: !parsedConfig.totalQuestions,
						expectedAnswers: !parsedConfig.expectedAnswers
					}
				});
				return defaultConfig;
			}

			return parsedConfig;

		} catch (error) {
			console.error('Quiz configuration parsing failed', {
				context: 'quiz-configuration-parsing',
				error: error.message,
				errorStack: error.stack
			});
			return defaultConfig;
		}
	}

	/**
	 * Initialize quiz functionality
	 * @private
	 */
	init() {
		// Validate initial state structure
		if (!this.state || !this.state.answers || !Array.isArray(this.state.history)) {
			const corruptedState = this.state;
			this.state = createQuizState();

			console.error('Quiz state corrupted on init', { corruptedState });
		}

		setInitialProgress(this.container);
		this.bindEvents();
		showQuestion(this.container, 1);
		updateProgress(this.container, 0, this.config);
	}

	/**
	 * Bind event listeners for quiz interactions
	 * @private
	 */
	bindEvents() {
		this.container.addEventListener('click', (e) => {
			// Defensive check - ensure quiz is fully initialized
			if (!this.state || !this.config) {
				console.warn('Quiz clicked before initialization complete', {
					hasState: !!this.state,
					hasConfig: !!this.config,
					action: e.target.closest('[data-quiz-action]')?.dataset.quizAction || 'unknown'
				});
				return;
			}

			const action = e.target.closest('[data-quiz-action]')?.dataset.quizAction;

			if (!action) return;

			switch (action) {
				case 'set-answer':
					this.handleAnswer(e.target.closest('[data-quiz-action]'));
					break;

				case 'go-back':
					this.goBack();
					break;

				case 'submit-email':
					this.handleEmailSubmission();
					break;
			}
		});
	}

	/**
	 * Handle answer selection and navigation
	 * @param {HTMLElement} element - The clicked answer element
	 * @private
	 */
	handleAnswer(element) {
		// Guard: Prevent double-clicks
		if (this.isProcessing) return;

		this.isProcessing = true;

		// Guard: Validate element exists
		if (!this.validateElement(element)) {
			this.resetProcessing();
			return;
		}

		// Extract and validate data attributes (data-quiz-key becomes dataset.quizKey)
		const { quizKey, quizValue, quizNext } = element.dataset;
		if (!this.validateDataAttributes(quizKey, quizValue, quizNext, element)) {
			this.resetProcessing();
			return;
		}

		// Update state
		if (!this.updateQuizState(quizKey, quizValue)) {
			this.resetProcessing();
			return;
		}

		// Handle navigation
		this.navigateQuiz(quizNext);

		// Reset processing flag after animation
		setTimeout(() => {
			this.isProcessing = false;
		}, 300);
	}

	/**
	 * Validate element exists
	 * @param {HTMLElement} element - Element to validate
	 * @returns {boolean} True if valid
	 * @private
	 */
	validateElement(element) {
		if (element) return true;

		console.error('handleAnswer called with null element - event delegation failed', {
			context: 'quiz-event-delegation-failure'
		});
		return false;
	}

	/**
	 * Validate data attributes
	 * @param {string} quizKey - Quiz key (from data-quiz-key)
	 * @param {string} quizValue - Quiz value (from data-quiz-value)
	 * @param {string} quizNext - Next navigation (from data-quiz-next)
	 * @param {HTMLElement} element - Element for error reporting
	 * @returns {boolean} True if valid
	 * @private
	 */
	validateDataAttributes(quizKey, quizValue, quizNext, element) {
		const isValid =
			typeof quizKey === 'string' && quizKey.trim() !== '' &&
			typeof quizValue === 'string' && quizValue.trim() !== '' &&
			typeof quizNext === 'string' && quizNext.trim() !== '';

		if (isValid) return true;

		console.error('Invalid or missing data attributes on quiz element', {
			quizKey,
			quizKeyType: typeof quizKey,
			quizValue,
			quizValueType: typeof quizValue,
			quizNext,
			quizNextType: typeof quizNext,
			expectedAttributes: ['data-quiz-key', 'data-quiz-value', 'data-quiz-next'],
			element: element.outerHTML?.substring(0, 200)
		});
		return false;
	}

	/**
	 * Update quiz state with answer
	 * @param {string} key - Question key
	 * @param {string} value - Answer value
	 * @returns {boolean} True if successful
	 * @private
	 */
	updateQuizState(key, value) {
		try {
			this.state = setAnswer(this.state, key, value);
			this.state = saveToHistory(this.state);
			return true;
		} catch (error) {
			console.error('Quiz state update failed', {
				context: 'quiz-state-update-failed',
				error: error.message,
				currentState: this.state,
				attemptedKey: key,
				attemptedValue: value
			});
			return false;
		}
	}

	/**
	 * Navigate to next question or result
	 * @param {string} next - Next navigation target
	 * @private
	 */
	navigateQuiz(next) {
		if (next === QUIZ_NAVIGATION.RESULT) {
			this.showResult();
			return;
		}

		const nextQuestion = parseInt(next);
		if (isNaN(nextQuestion)) {
			console.error('Invalid next question number', { next });
			this.resetProcessing();
			return;
		}

		try {
			this.state = setCurrentQuestion(this.state, nextQuestion);
			showQuestion(this.container, nextQuestion);
			updateProgress(
				this.container,
				Object.keys(this.state.answers).length,
				this.config
			);
		} catch (error) {
			console.error('Quiz navigation failed', {
				context: 'quiz-navigation-failed',
				error: error.message,
				nextQuestion,
				currentState: this.state
			});
			this.resetProcessing();
		}
	}

	/**
	 * Reset processing flag immediately
	 * @private
	 */
	resetProcessing() {
		this.isProcessing = false;
	}

	/**
	 * Navigate back to previous question
	 * @private
	 */
	goBack() {
		// Guard: Prevent double-clicks during navigation
		if (this.isProcessing) return;

		this.isProcessing = true;

		try {
			const previousState = restoreFromHistory(this.state);
			if (previousState === this.state) {
				this.resetProcessing();
				return; // No history
			}

			this.state = previousState;
			showQuestion(this.container, this.state.currentQuestion);
			updateProgress(
				this.container,
				Object.keys(this.state.answers).length,
				this.config
			);
		} catch (error) {
			console.error('Quiz go back failed', {
				context: 'quiz-go-back-failed',
				error: error.message,
				currentState: this.state
			});
		} finally {
			// Reset after animation completes
			setTimeout(() => {
				this.isProcessing = false;
			}, 300);
		}
	}

	/**
	 * Show quiz result and update UI
	 * @private
	 */
	showResult() {
		const resultType = calculateResult(this.state.answers);
		const resultMessage = getResultMessage(resultType);

		updateTitle(this.container, resultType);
		setProgressTo100(this.container);
		showResult(this.container, resultMessage);
	}

	/**
	 * Handle email submission and validation
	 * @private
	 */
	async handleEmailSubmission() {
		const email = getValidatedEmail(this.container);
		if (!email) return;

		const quizData = {
			answers: this.state.answers,
			questionsAnswered: getQuestionsAnswered(this.state.answers),
			result: calculateResult(this.state.answers),
			email: email,
			timestamp: new Date().toISOString()
		};

		try {
			await submitToZapier(quizData);
			showSubmissionSuccess(this.container);
		} catch (error) {
			console.error('Quiz Zapier submission failed', {
				context: 'quiz-zapier-submission-failed',
				error: error.message,
				quizData: {
					...quizData,
					email: '[REDACTED]' // Don't log PII to console
				}
			});
			alert('There was a problem submitting your quiz. Please try again or contact support.');
		}
	}
}

// Initialize quiz when component is present (with guard to prevent double initialization)
let quizInitialized = false;

function initializeQuiz() {
	// Prevent multiple initializations
	if (quizInitialized) {
		return;
	}

	const quizContainer = document.querySelector(
		'[data-component="annuity-quiz"]'
	);

	if (quizContainer) {
		// Check if quiz was already initialized on this container
		if (quizContainer.dataset.quizInitialized === 'true') {
			return;
		}

		// Mark as initialized
		quizInitialized = true;
		quizContainer.dataset.quizInitialized = 'true';

		// Add a small delay to ensure DOM is fully ready
		requestAnimationFrame(() => {
			new AnnuityQuiz();
		});
	}
}

// Multiple initialization strategies for reliability
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initializeQuiz);
} else if (document.readyState === 'interactive') {
	// DOM is loaded but resources might still be loading
	setTimeout(initializeQuiz, 0);
} else {
	// DOM and resources are fully loaded
	initializeQuiz();
}
